import React, {useEffect, useMemo, useState} from 'react';
import {
	StackCompat as Stack,
	TextCompat as Text,
} from 'components/FluentUICompat';
import {useTranslation} from 'react-i18next';
import {useVkoKeywordsLazyQuery} from 'features/Dashboard/hooks/vkoKeywords.generated';
import {VkoApproverMyTodoTile} from './VkoApproverMyTodoTile';
import {useVkoApproverWorkbenchStyles} from './useVkoApproverWorkbenchStyles';
import {VkoApproverInputTile} from './VkoApproverInputTile';
import {VkoParagraphsWorkbenchItems} from '../VkoWorkbench/VkoParagraphsWorkbenchItems';
import {MyReviewItems} from '../ApproverWorkbench/MyReviewItems';
import {SummaryComponent} from '../Common/SummaryComponent';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';

const VkoApproverWorkbenchComponent: React.FC = () => {
	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'DashboardPage',
	});
	const {classNames, paddingTop, width33} = useVkoApproverWorkbenchStyles();

	const [getVkoKeywords] = useVkoKeywordsLazyQuery();
	const {getVkoApproverMyScopeDocCount} = useDashboardQueries();
	const myScopeDocCount = getVkoApproverMyScopeDocCount();

	useEffect(() => {
		getVkoKeywords().catch(error => {
			console.error('Error fetching Vko keywords:', error);
		});
	}, [getVkoKeywords]);

	const [approverReviewCount, setApproverReviewCount] = useState<
		number | undefined
	>(undefined);

	const [todoItemCount, setTodoItemCount] = useState<number | undefined>(
		undefined,
	);
	const [approverInputCount, setApproverInputCount] = useState<
		number | undefined
	>(undefined);

	const myRegulationsCounter = useMemo(() => {
		if (myScopeDocCount === undefined) return <div>?</div>;
		return (
			<a
				href='/regulations?view=MY_DOC'
				style={{textDecoration: 'none', color: 'inherit'}}
			>
				{myScopeDocCount}
			</a>
		);
	}, [myScopeDocCount]);

	return (
		<>
			<Stack className={classNames.mainHeaderContainer} horizontal>
				<Stack.Item>
					<SummaryComponent requirementCount={undefined} />
				</Stack.Item>
				<Stack.Item
					styles={{
						root: {
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							paddingRight: 40,
						},
					}}
				>
					<Text variant='large'>{t('InMyScope')}</Text>
					<Text variant='large' className={classNames.primaryCounter}>
						{myRegulationsCounter}
					</Text>
				</Stack.Item>
			</Stack>
			<Stack
				horizontal
				horizontalAlign='start'
				className={classNames.rowSectionContainer}
			>
				<Stack.Item className={classNames.rowSectionContainerContent}>
					<div>{t('MyProcessingRegulations')}</div>
				</Stack.Item>
			</Stack>
			<Stack
				horizontal
				horizontalAlign='start'
				className={classNames.cardContainer}
			>
				<Stack.Item styles={{root: {...paddingTop, ...width33}}} grow={1}>
					<VkoApproverMyTodoTile
						halfHeight={true}
						title={t('MyTodos')}
						t={t}
						itemCountLoaded={(count: number) => setTodoItemCount(count)}
					/>
				</Stack.Item>
				<Stack.Item styles={{root: {...paddingTop, ...width33}}} grow={1}>
					<VkoApproverInputTile
						halfHeight={true}
						title={t('WaitingForInput')}
						t={t}
						itemCountLoaded={(count: number) => setApproverInputCount(count)}
					/>
				</Stack.Item>
				<Stack.Item styles={{root: {...paddingTop, ...width33}}} grow={1}>
					<VkoParagraphsWorkbenchItems
						title={t('RejectedKeywords')}
						halfHeight
						isVkoApproverDashboard={true}
					/>
				</Stack.Item>
			</Stack>
			<Stack
				horizontal
				horizontalAlign='start'
				className={classNames.rowSectionContainer}
			>
				<Stack.Item className={classNames.rowSectionContainerContent}>
					<div>{t('RequirementsToBeReviewedByMe')}</div>
				</Stack.Item>
			</Stack>
			<Stack
				horizontal
				horizontalAlign='start'
				className={classNames.cardContainer}
			>
				<Stack.Item styles={{root: paddingTop}} grow={1}>
					<MyReviewItems
						halfHeight
						itemCountLoaded={(count: number) => setApproverReviewCount(count)}
						isVkoApproverDashboard={true}
						enforce3columns={true}
					/>
				</Stack.Item>
			</Stack>
		</>
	);
};

export default VkoApproverWorkbenchComponent;
